import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";

import { LoggerService } from "@core/services/logger.service";

import { CustomIconName, customIcons } from "../../icons/icon-definitions";

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "custom-icon",
  template: `
    <svg
      [attr.width]="size()"
      [attr.height]="height() ?? size()"
      [attr.stroke-width]="strokeWidth()"
      [attr.viewBox]="viewBox()"
      xmlns="http://www.w3.org/2000/svg"
      [style.color]="color()"
      [innerHTML]="iconSvgContent()"></svg>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomIconComponent {
  readonly name = input.required<CustomIconName>();
  readonly size = input<number | string>(24);
  readonly height = input<number | string>();
  readonly strokeWidth = input<number | string>(2);
  readonly color = input<string>("currentColor");
  readonly viewBox = input<string>("0 0 24 24");
  private readonly _logger = inject(LoggerService);

  private readonly _sanitizer = inject(DomSanitizer);

  protected readonly iconSvgContent = computed(() => {
    const iconData = customIcons[this.name()];
    if (!iconData) {
      this._logger.warn(`Custom icon "${this.name()}" not found`);
      return this._sanitizer.bypassSecurityTrustHtml('<circle cx="12" cy="12" r="3"></circle><path d="M12 1v6m0 6v6"></path>');
    }

    // Convert Lucide icon data format to SVG string
    const svgContent = this.convertIconDataToSvg(iconData);

    // Bypass Angular's sanitizer for trusted SVG content
    return this._sanitizer.bypassSecurityTrustHtml(svgContent);
  });

  private convertIconDataToSvg(iconData: any[]): string {
    return iconData
      .map(([tag, attributes]) => {
        if (!attributes) {
          return `<${tag}></${tag}>`;
        }

        const attrs = Object.entries(attributes)
          .map(([key, value]) => {
            // Handle special cases for attribute names
            const attrName =
              key === "fillRule"
                ? "fill-rule"
                : key === "clipRule"
                  ? "clip-rule"
                  : key === "strokeWidth"
                    ? "stroke-width"
                    : key === "strokeLinecap"
                      ? "stroke-linecap"
                      : key === "strokeLinejoin"
                        ? "stroke-linejoin"
                        : key;
            return `${attrName}="${value}"`;
          })
          .join(" ");
        return `<${tag} ${attrs}></${tag}>`;
      })
      .join("");
  }
}
